import { type AxiosResponse } from "axios";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { useSWRConfig } from "swr";
import { toast } from "react-toastify";
import { type ICookiesType } from "../types/cookies";
import { type IJwtResponse } from "../types/jwt";
import { useApiClient } from "./useApiClient";
import { useUserMe } from "@/features/login/hooks/useUserMe";
import { useLocalStorage } from "./useLocalStorage";
import { type User } from "@/types/localstorage";
import { useGeoSpaceApi } from "@/features/map/hooks/useGeospaceSearch";
import { clearAuthData } from "@/utils/auth";
import { isIpNotAllowedState } from "@/utils/ipRestriction";
import { saveMapServiceContractToLocalStorage } from "@/utils/mapServiceContract";

/**
 * ログイン失敗時のメッセージ
 *
 * 許可IP外・パスワード不一致・未登録メール・契約終了のいずれもバックエンドは同一の401を
 * 返すため、FE側でも理由を判別せず常にこの文言を出す。レスポンスの内容で出し分けると、
 * その分岐が「どのアカウント・どのパスワードが正しいか」を判別する材料になる
 */
const LOGIN_FAILED_MESSAGE =
  "ログインできませんでした。メールアドレスとパスワードをご確認ください。なお、ご契約企業で許可されたネットワーク以外からはご利用いただけない場合があります。";

interface ReturnTypes {
  isLoggedIn: boolean;
  login: (
    email: string,
    password: string,
    userIp: string,
    cancelLoading: () => void
  ) => Promise<void>;
  logout: () => void;
}
export const useLogin = (): ReturnTypes => {
  const [cookies, setCookie, removeCookie] = useCookies(); // eslint-disable-line
  const typedCookies = cookies as ICookiesType;
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  const { apiClient } = useApiClient();
  const { userMe } = useUserMe();
  const [, , removeValue] = useLocalStorage<User | undefined>(
    "user",
    undefined
  );
  const { geospaceGetMapServiceContract } = useGeoSpaceApi();

  const login = async (
    email: string,
    password: string,
    userIp: string,
    cancelLoading: () => void
  ): Promise<void> => {
    await apiClient
      .post("auth/jwt/create", {
        email,
        password,
        userIp,
      })
      .then(async (response: AxiosResponse<IJwtResponse>) => {
        setCookie("accesstoken", response.data.access, { path: "/" });
        setCookie("refreshtoken", response.data.refresh, { path: "/" });
        userMe();
        const mapServiceContract = await geospaceGetMapServiceContract();

        // 許可IP外ではログインが通っても後続の取得は403になる。取得失敗を
        // 「契約なし」として保存すると既存の契約情報を消してしまうため、
        // 拒否された場合は残しておき、専用画面での再開時に取り直す
        if (!isIpNotAllowedState()) {
          saveMapServiceContractToLocalStorage(mapServiceContract ?? null);
        }

        setCookie("isLogin", "true", { path: "/" });
      })
      .catch(() => {
        toast.error(LOGIN_FAILED_MESSAGE);
      })
      .finally(() => {
        cancelLoading();
      });
  };

  const logout = (): void => {
    clearAuthData(removeCookie, setCookie, removeValue);
    // 別ユーザーで入り直した際に前ユーザーの現在ユーザー情報（会員判定含む）が
    // 残らないよう、SWR キャッシュを破棄する。
    void mutate(() => true, undefined, { revalidate: false });
    navigate("/login");
  };

  return {
    isLoggedIn: typedCookies.isLogin === "true",
    login,
    logout,
  };
};
